import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, ScrollView } from 'react-native';

interface SleekTimePickerProps {
    startTime?: string;
    endTime?: string;
    onTimeChange: (startTime: string, endTime: string) => void;
}

const SleekTimePicker: React.FC<SleekTimePickerProps> = ({
    startTime,
    endTime,
    onTimeChange
}) => {
    const [selectedStartHour, setSelectedStartHour] = useState(9);
    const [selectedStartMinute, setSelectedStartMinute] = useState(0);
    const [selectedStartPeriod, setSelectedStartPeriod] = useState<'AM' | 'PM'>('AM');
    
    const [selectedEndHour, setSelectedEndHour] = useState(5);
    const [selectedEndMinute, setSelectedEndMinute] = useState(0);
    const [selectedEndPeriod, setSelectedEndPeriod] = useState<'AM' | 'PM'>('PM');

    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10, 15, ..., 55

    // Parse existing time strings
    useEffect(() => {
        if (startTime) {
            const parsed = parseTimeString(startTime);
            if (parsed) {
                setSelectedStartHour(parsed.hour);
                setSelectedStartMinute(parsed.minute);
                setSelectedStartPeriod(parsed.period);
            }
        }
        if (endTime) {
            const parsed = parseTimeString(endTime);
            if (parsed) {
                setSelectedEndHour(parsed.hour);
                setSelectedEndMinute(parsed.minute);
                setSelectedEndPeriod(parsed.period);
            }
        }
    }, [startTime, endTime]);

    // Update parent when times change
    useEffect(() => {
        const startTimeString = formatTime(selectedStartHour, selectedStartMinute, selectedStartPeriod);
        const endTimeString = formatTime(selectedEndHour, selectedEndMinute, selectedEndPeriod);
        onTimeChange(startTimeString, endTimeString);
    }, [selectedStartHour, selectedStartMinute, selectedStartPeriod, selectedEndHour, selectedEndMinute, selectedEndPeriod, onTimeChange]);

    const parseTimeString = (timeStr: string) => {
        const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
        if (match) {
            return {
                hour: parseInt(match[1]),
                minute: parseInt(match[2]),
                period: match[3].toUpperCase() as 'AM' | 'PM'
            };
        }
        return null;
    };

    const formatTime = (hour: number, minute: number, period: 'AM' | 'PM') => {
        return `${hour}:${minute.toString().padStart(2, '0')} ${period}`;
    };

    const renderTimeSelector = (
        type: 'start' | 'end',
        selectedHour: number,
        selectedMinute: number,
        selectedPeriod: 'AM' | 'PM',
        setHour: (hour: number) => void,
        setMinute: (minute: number) => void,
        setPeriod: (period: 'AM' | 'PM') => void
    ) => {
        return (
            <View style={styles.timeSelectorContainer}>
                <Text style={styles.timeSelectorLabel}>
                    {type === 'start' ? 'Start Time' : 'End Time'}
                </Text>
                
                <View style={styles.timeDisplay}>
                    <Text style={styles.selectedTime}>
                        {formatTime(selectedHour, selectedMinute, selectedPeriod)}
                    </Text>
                </View>

                <View style={styles.timeControls}>
                    {/* Hour Selection */}
                    <View style={styles.timeColumn}>
                        <Text style={styles.columnLabel}>Hour</Text>
                        <ScrollView 
                            style={styles.scrollContainer}
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={styles.scrollContent}
                        >
                            {hours.map((hour) => (
                                <Pressable
                                    key={hour}
                                    style={[
                                        styles.timeOption,
                                        selectedHour === hour && styles.selectedTimeOption
                                    ]}
                                    onPress={() => setHour(hour)}
                                >
                                    <Text style={[
                                        styles.timeOptionText,
                                        selectedHour === hour && styles.selectedTimeOptionText
                                    ]}>
                                        {hour}
                                    </Text>
                                </Pressable>
                            ))}
                        </ScrollView>
                    </View>

                    {/* Minute Selection */}
                    <View style={styles.timeColumn}>
                        <Text style={styles.columnLabel}>Minute</Text>
                        <ScrollView 
                            style={styles.scrollContainer}
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={styles.scrollContent}
                        >
                            {minutes.map((minute) => (
                                <Pressable
                                    key={minute}
                                    style={[
                                        styles.timeOption,
                                        selectedMinute === minute && styles.selectedTimeOption
                                    ]}
                                    onPress={() => setMinute(minute)}
                                >
                                    <Text style={[
                                        styles.timeOptionText,
                                        selectedMinute === minute && styles.selectedTimeOptionText
                                    ]}>
                                        {minute.toString().padStart(2, '0')}
                                    </Text>
                                </Pressable>
                            ))}
                        </ScrollView>
                    </View>

                    {/* AM/PM Selection */}
                    <View style={styles.timeColumn}>
                        <Text style={styles.columnLabel}>Period</Text>
                        <View style={styles.periodContainer}>
                            <Pressable
                                style={[
                                    styles.periodButton,
                                    selectedPeriod === 'AM' && styles.selectedPeriodButton
                                ]}
                                onPress={() => setPeriod('AM')}
                            >
                                <Text style={[
                                    styles.periodText,
                                    selectedPeriod === 'AM' && styles.selectedPeriodText
                                ]}>
                                    AM
                                </Text>
                            </Pressable>
                            <Pressable
                                style={[
                                    styles.periodButton,
                                    selectedPeriod === 'PM' && styles.selectedPeriodButton
                                ]}
                                onPress={() => setPeriod('PM')}
                            >
                                <Text style={[
                                    styles.periodText,
                                    selectedPeriod === 'PM' && styles.selectedPeriodText
                                ]}>
                                    PM
                                </Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            </View>
        );
    };

    return (
        <View style={styles.container}>
            {/* Start Time */}
            {renderTimeSelector(
                'start',
                selectedStartHour,
                selectedStartMinute,
                selectedStartPeriod,
                setSelectedStartHour,
                setSelectedStartMinute,
                setSelectedStartPeriod
            )}

            {/* Divider */}
            <View style={styles.divider} />

            {/* End Time */}
            {renderTimeSelector(
                'end',
                selectedEndHour,
                selectedEndMinute,
                selectedEndPeriod,
                setSelectedEndHour,
                setSelectedEndMinute,
                setSelectedEndPeriod
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
    },
    timeSelectorContainer: {
        marginBottom: 16,
    },
    timeSelectorLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: '#222',
        marginBottom: 12,
        textAlign: 'center',
    },
    timeDisplay: {
        backgroundColor: '#f7f7f7',
        borderRadius: 12,
        paddingVertical: 16,
        paddingHorizontal: 24,
        marginBottom: 20,
        alignItems: 'center',
    },
    selectedTime: {
        fontSize: 24,
        fontWeight: '600',
        color: '#222',
    },
    timeControls: {
        flexDirection: 'row',
        gap: 16,
    },
    timeColumn: {
        flex: 1,
        alignItems: 'center',
    },
    columnLabel: {
        fontSize: 12,
        fontWeight: '500',
        color: '#717171',
        marginBottom: 8,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    scrollContainer: {
        height: 120,
        width: '100%',
    },
    scrollContent: {
        paddingVertical: 8,
        alignItems: 'center',
    },
    timeOption: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 8,
        marginVertical: 2,
        minWidth: 50,
        alignItems: 'center',
    },
    selectedTimeOption: {
        backgroundColor: '#222',
    },
    timeOptionText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    selectedTimeOptionText: {
        color: 'white',
        fontWeight: '600',
    },
    periodContainer: {
        gap: 8,
        alignItems: 'center',
        paddingTop: 8,
    },
    periodButton: {
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#e9ecef',
        backgroundColor: 'white',
        minWidth: 60,
        alignItems: 'center',
    },
    selectedPeriodButton: {
        backgroundColor: '#222',
        borderColor: '#222',
    },
    periodText: {
        fontSize: 14,
        fontWeight: '500',
        color: '#666',
    },
    selectedPeriodText: {
        color: 'white',
        fontWeight: '600',
    },
    divider: {
        height: 1,
        backgroundColor: '#e9ecef',
        marginVertical: 24,
    },
});

export default SleekTimePicker; 